import React, { forwardRef, useRef, useState } from 'react';
import { IconCalendar, IconClose, theme, useLocalizationContext, } from '@contrast-security-inc/design-system-foundations';
import { TextInput } from '../TextInput';
import { useDateFormatter } from '../DateModules/useDateFormatter';
import { DatePicker } from '../DatePicker';
import { css, cx } from '@emotion/css';
import * as messages from '../DateModules/translations';
import { usePopover, Popover } from '@contrast-security-inc/design-system-popovers';
const dateClearButtonStyles = (isDisabled) => css `
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};

  &:focus-visible {
    border-radius: 6px;
    border-color: ${theme.color.turquoise70};
    outline: unset;
  }
`;
const dateSelectButtonStyles = (isDisabled) => css `
  background-color: unset;
  border: 1px solid transparent;
  padding: 0.5rem;
  display: flex;
  margin-right: 0.5rem;
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
`;
export const DateSelectButton = forwardRef(({ onClick, testId, isDisabled }, ref) => {
    const { formatMessage } = useLocalizationContext();
    return (React.createElement("button", { tabIndex: -1, "aria-label": formatMessage(messages, 'calendar'), "data-testid": `${testId}-calendar-button`, type: "button", ref: ref, className: dateSelectButtonStyles(isDisabled), onClick: onClick },
        React.createElement(IconCalendar, { size: "sm" })));
});
DateSelectButton.displayName = 'DateSelectButton';
export const DateClearButton = forwardRef(({ onClick, testId, isDisabled }, ref) => {
    const { formatMessage } = useLocalizationContext();
    return (React.createElement("button", { "aria-label": formatMessage(messages, 'clearDate'), type: "button", "data-testid": `${testId}-clear-button`, ref: ref, className: cx(dateSelectButtonStyles(isDisabled), dateClearButtonStyles(isDisabled)), onClick: onClick },
        React.createElement(IconClose, { size: "sm" })));
});
DateClearButton.displayName = 'DateClearButton';
const isBlurWithinDatePicker = (event, popoverTrigger) => {
    const popover = document.querySelector('[data-popper]');
    return popover?.contains(event.relatedTarget) || popoverTrigger === event.relatedTarget;
};
const createCustomEvent = (date) => Object.create(new Event('changeEvent'), {
    target: {
        value: { value: date },
    },
});
const focusInput = (event, containerRef) => {
    event.preventDefault();
    containerRef?.current?.querySelector('input')?.focus();
};
export const DateInput = forwardRef(({ value, onChange, testId = 'date-select', error: errorProp, isDisabled, initialMonth, onClear, minDate, maxDate, className, ...rest }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState();
    const { getDatePlaceholder } = useLocalizationContext();
    const format = getDatePlaceholder();
    const containerRef = useRef(null);
    const { popoverTriggerProps, popoverProps } = usePopover({ hasArrow: true, placement: 'bottom-end' });
    const handleChange = (date) => {
        onChange(createCustomEvent(date), date);
        setError('');
    };
    const { inputValue, onInputChange } = useDateFormatter({
        value,
        onChange: handleChange,
        onError: setError,
    });
    const handleFocus = () => {
        setIsOpen(true);
    };
    const handleBlur = (event) => {
        if (isBlurWithinDatePicker(event, popoverProps.referenceElement)) {
            return;
        }
        setIsOpen(false);
    };
    const handleDateSelected = (date) => {
        onChange(createCustomEvent(date), date);
        setError('');
        setIsOpen(false);
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
            return;
        }
        setIsOpen(true);
    };
    const handleClear = (event) => {
        onInputChange(Object.create(new Event('changeEvent'), {
            target: {
                value: { value: '' },
            },
        }));
        focusInput(event, containerRef);
        if (onClear) {
            onClear();
        }
    };
    const getContainerProps = () => {
        if (isDisabled) {
            return {
                onFocus: undefined,
                onBlur: handleBlur,
                onKeyDown: undefined,
            };
        }
        return {
            onFocus: handleFocus,
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
        };
    };
    return (React.createElement(TextInput, { ...rest, isDisabled: isDisabled, testId: testId, placeholder: format, onChange: onInputChange, ContainerProps: getContainerProps(), autoComplete: "off", value: inputValue, containerRef: containerRef, className: cx(className, 'ds-date-input'), ref: ref, type: "tel", error: errorProp || error, endAdornment: React.createElement(React.Fragment, null,
            inputValue ? (React.createElement(DateClearButton, { testId: testId, isDisabled: isDisabled, onClick: !isDisabled ? handleClear : undefined, ref: popoverTriggerProps.ref })) : (React.createElement(DateSelectButton, { ref: popoverTriggerProps.ref, testId: testId, isDisabled: isDisabled, onClick: !isDisabled
                    ? (event) => {
                        focusInput(event, containerRef);
                    }
                    : undefined })),
            React.createElement(Popover, { ...popoverProps, isOpen: isOpen, offset: [0, 8], "data-popper": true }, isOpen && (React.createElement(DatePicker, { date: value, onDateSelected: handleDateSelected, testId: `${testId}-date-picker`, initialMonth: initialMonth, minDate: minDate, maxDate: maxDate })))) }));
});
DateInput.displayName = 'DateInput';
